import React, { useState } from 'react';

import { Chip } from '../../ui';

/**
 * A miniatura de uma foto de vistoria — **um componente para as três grades**
 * (at-63, frente D/D2).
 *
 * Medido antes: a mesma miniatura existia em **três** lugares, cada um com uma
 * forma de dizer "esta foto tem avaria" e dois com o próprio `onError`:
 *
 *   • `InspectionCapture` — fotos novas desta sessão (legenda, selo de avaria,
 *     coordenadas e quatro botões);
 *   • `vistoriaCaptura` — fotos **já no banco** de um rascunho retomado (legenda
 *     e "Remover"), onde a avaria era anunciada por um `::before` de CSS com o
 *     texto `'AVARIA · '`;
 *   • o documento do laudo — legenda e selo (entra na V2 desta frente).
 *
 * 🔴 **O selo é TEXTO** ("Avaria"). A moldura vermelha é reforço, nunca a única
 * marca — spec 02 §9 (*"legíveis sem depender só de cor — daltonismo, sol na
 * tela"*), a mesma regra que fez o `Chip` nascer com texto obrigatório.
 *
 * 🔴 Mora em `components/` e não em `src/ui` porque conhece o domínio: sabe o que
 * é uma avaria e o que são as coordenadas de captura. O `Chip` que ele usa é puro
 * — mesma relação do `FormularioAssociado` (at-58) com o kit.
 *
 * ⚠️ **Componente burro**: não busca nada, não normaliza URL (quem faz isso é
 * `utils/vistoriaImagens.ts`) e não decide a ordem das fotos.
 */

interface PropsMiniaturaFotoVistoria {
  /** URL já normalizada. Pode ser `blob:` (captura local) ou do GCS. */
  src?: string;
  /** A legenda do slot — vai no `alt`, porque é o que a foto mostra. */
  legenda: string;
  /** A marcação está **queimada** na imagem (at-24); isto só a anuncia. */
  avaria?: boolean;
  /** Latitude/longitude da captura, quando o aparelho as forneceu. */
  coordenadas?: { latitude: number | null; longitude: number | null };
  /** Botões da miniatura (Visualizar, Marcar Avaria, Remover…). */
  acoes?: React.ReactNode;
}

const MiniaturaFotoVistoria: React.FC<PropsMiniaturaFotoVistoria> = ({
  src,
  legenda,
  avaria = false,
  coordenadas,
  acoes,
}) => {
  /**
   * 🔴 A imagem que não carrega **diz isso por extenso**.
   *
   * O caminho antigo escondia o `<img>` e acrescentava uma classe ao bloco por
   * `closest()` + `classList.add` — manipulação direta do DOM, que o React
   * desfaz no próximo render, e que dependia de o pai ter a classe certa. Aqui é
   * estado: a foto sai e entra um texto no lugar, então "não carregou" nunca se
   * confunde com "não tem foto".
   */
  const [indisponivel, setIndisponivel] = useState(false);
  const semImagem = !src || indisponivel;

  return (
    <div
      className={[
        'tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-white',
        avaria
          ? 'tw-border-2 tw-border-danger tw-bg-danger-bg'
          : 'tw-border tw-border-superficie-borda',
      ].join(' ')}
    >
      {semImagem ? (
        <div className="tw-flex tw-h-24 tw-items-center tw-justify-center tw-bg-superficie tw-px-2 tw-text-center tw-text-[10px] tw-text-texto-suave">
          Imagem indisponível
        </div>
      ) : (
        <img
          src={src}
          alt={legenda}
          onError={() => setIndisponivel(true)}
          className="tw-h-24 tw-w-full tw-bg-superficie tw-object-cover"
        />
      )}

      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-p-2">
        <p className="tw-m-0 tw-break-words tw-text-[10px] tw-font-semibold tw-leading-tight tw-text-texto-corpo">
          {legenda || '—'}
        </p>

        {avaria && (
          <Chip variante="perigo" className="tw-self-start">
            Avaria
          </Chip>
        )}

        {coordenadas &&
          (coordenadas.latitude !== null && coordenadas.longitude !== null ? (
            <p className="tw-m-0 tw-break-words tw-font-mono tw-text-[9px] tw-text-texto-suave">
              Lat {coordenadas.latitude.toFixed(5)} · Lon {coordenadas.longitude.toFixed(5)}
            </p>
          ) : (
            /*
             * ⚠️ Dizer que a localização faltou é informação, não erro: o laudo
             * sai sem ela e a vistoria segue. Era um texto laranja em itálico
             * ("Localização não disponível"), e laranja é a cor da marca — não a
             * de aviso (`13-ui-kit.md`).
             */
            <p className="tw-m-0 tw-text-[9px] tw-text-texto-desabilitado">
              Localização não disponível
            </p>
          ))}

        {acoes && <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-1">{acoes}</div>}
      </div>
    </div>
  );
};

export default MiniaturaFotoVistoria;
